/**
 * PhotoCapture — camera/file input for comparator photos (bonus E).
 */
import { useRef } from 'react';
import { resizePhoto } from './photo-utils';

export interface PhotoCaptureProps {
  existingDataUrl?: string;
  onCapture(dataUrl: string | undefined): void;
}

export function PhotoCapture({
  existingDataUrl,
  onCapture,
}: PhotoCaptureProps) {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizePhoto(file);
      onCapture(dataUrl);
    } catch {
      // silently fail — user can retry
    }
  };

  return (
    <div className="mt-4">
      {existingDataUrl ? (
        <div className="space-y-2">
          <img
            src={existingDataUrl}
            alt="Attached comparator photo"
            className="max-w-full rounded-lg border border-slate-600"
          />
          <button
            type="button"
            onClick={() => onCapture(undefined)}
            className="text-xs text-red-400 hover:text-red-300"
          >
            Remove photo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors"
        >
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          &#128247; Attach photo (optional)
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void handleFile(e)}
      />
    </div>
  );
}
